import { useState, useEffect } from 'react';
import apiService from '../services/api';

/**
 * Password policy fetched from the backend.
 *
 * The server is the only authority on password validity. This hook exists so
 * the UI displays the CURRENT rules rather than hardcoded ones — it does not
 * and cannot fully validate. Breach screening and context screening
 * (name/email in password) run server-side only, so a password that looks
 * valid here may still be rejected on submit. Always surface the server's
 * error message.
 */
export interface PasswordPolicy {
  minLength: number;
  maxLength: number;
  requiresUppercase: boolean;
  requiresLowercase: boolean;
  requiresNumber: boolean;
  requiresSpecial: boolean;
  screensBreachedPasswords: boolean;
  rules: string[];
  guidance: string;
}

/**
 * Conservative fallback used when the policy endpoint is unreachable.
 * Deliberately matches the server minimum so the UI never advertises a
 * weaker rule than is actually enforced.
 */
export const FALLBACK_POLICY: PasswordPolicy = {
  minLength: 12,
  maxLength: 64,
  requiresUppercase: false,
  requiresLowercase: false,
  requiresNumber: false,
  requiresSpecial: false,
  screensBreachedPasswords: true,
  rules: [
    'At least 12 characters',
    'Any letters, numbers, symbols, or spaces — a passphrase works well',
    'Not a password known from a public data breach',
    'Does not contain your name or email address',
  ],
  guidance:
    'Longer is stronger. Four or five random words are easier to remember ' +
    'and harder to crack than a short password with symbols.',
};

// Module-level cache — the policy is identical for every user and changes
// only on deploy. The endpoint also sends Cache-Control: max-age=3600.
let cachedPolicy: PasswordPolicy | null = null;
let inflight: Promise<PasswordPolicy> | null = null;

function fetchPolicy(): Promise<PasswordPolicy> {
  if (cachedPolicy) return Promise.resolve(cachedPolicy);
  if (inflight) return inflight;

  inflight = apiService
    .get('/auth/password-policy')
    .then((res: any) => {
      const policy = res?.data as PasswordPolicy;
      if (!policy || typeof policy.minLength !== 'number') {
        throw new Error('Malformed password policy response');
      }
      cachedPolicy = policy;
      return policy;
    })
    .catch(() => {
      // Never block the form on a failed policy fetch.
      inflight = null;
      return FALLBACK_POLICY;
    });

  return inflight;
}

/**
 * @returns { policy, loaded } — `policy` is always usable; it starts as the
 * fallback and is replaced once the server responds.
 */
export function usePasswordPolicy() {
  const [policy, setPolicy] = useState<PasswordPolicy>(cachedPolicy || FALLBACK_POLICY);
  const [loaded, setLoaded] = useState<boolean>(cachedPolicy !== null);

  useEffect(() => {
    let active = true;
    fetchPolicy().then((p) => {
      if (active) {
        setPolicy(p);
        setLoaded(true);
      }
    });
    return () => {
      active = false;
    };
  }, []);

  return { policy, loaded };
}

/**
 * Client-side checks only. Mirrors the subset of server rules that can be
 * evaluated locally — length, and composition if the policy ever re-enables
 * it. Returns `meetsClientChecks`, deliberately NOT named "valid": passing
 * here does not guarantee the server will accept the password.
 */
export function evaluatePassword(password: string, policy: PasswordPolicy) {
const chars = Array.from(password);

  const checks: Array<{ key: string; label: string; ok: boolean }> = [
    {
      key: 'length',
      label: `${policy.minLength}+ characters`,
      ok: chars.length >= policy.minLength,
    },
  ];

  if (policy.maxLength && chars.length > policy.maxLength) {
    checks.push({
      key: 'maxLength',
      label: `No more than ${policy.maxLength} characters`,
      ok: false,
    });
  }

  if (policy.requiresUppercase) {
    checks.push({ key: 'uppercase', label: 'Uppercase', ok: /[A-Z]/.test(password) });
  }
  if (policy.requiresLowercase) {
    checks.push({ key: 'lowercase', label: 'Lowercase', ok: /[a-z]/.test(password) });
  }
  if (policy.requiresNumber) {
    checks.push({ key: 'number', label: 'Number', ok: /[0-9]/.test(password) });
  }
  if (policy.requiresSpecial) {
    checks.push({
      key: 'special',
      label: 'Special character',
      ok: /[^A-Za-z0-9]/.test(password),
    });
  }

  return {
    checks,
    meetsClientChecks: checks.every((c) => c.ok),
  };
}

export default usePasswordPolicy;
